<main class="bg-black text-white min-h-screen overflow-x-hidden">

  <!-- Hero -->
  <section class="relative min-h-[65vh] flex items-center pt-24 pb-14 overflow-hidden page-hero-gradient">
    <div class="absolute -top-48 right-0 w-[700px] h-[700px] bg-varroc-accent/5 rounded-full blur-3xl"></div>
    <div class="absolute inset-0 opacity-[0.025]" style="background-image: linear-gradient(rgba(0,170,230,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,230,0.5) 1px, transparent 1px); background-size: 60px 60px;"></div>

    <div class="relative z-10 max-w-7xl mx-auto px-6 w-full">
      <div class="max-w-3xl">
        <a routerLink="/products/connectiq" class="back-nav" data-aos="fade-down">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 svg-stroke-gradient"><polyline points="15 18 9 12 15 6"></polyline></svg>
          Back
        </a>
        <h1 class="hero-col font-black mb-5" data-aos="fade-up" data-aos-delay="100">
          CAP <span class="text-varroc-accent">Customer Adoption</span> Program
        </h1>
        <p class="text-varroc-text-gray text-lg leading-relaxed mb-8 max-w-2xl" data-aos="fade-up" data-aos-delay="200">
          CAP is Varroc Connect's unified customer-centric engagement framework designed to power the entire connected-vehicle lifecycle, from onboarding to adoption, engagement, analytics, monetization, and lifecycle management.
        </p>
        <div class="flex flex-wrap gap-4" data-aos="fade-up" data-aos-delay="300">
          <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-8 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all cap_customer_adoption_program_talk_to_our_team_button">
            Talk to Our Team
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Sticky Nav -->
  <nav class="sticky-product-nav">
    <div class="max-w-7xl mx-auto px-6">
      <div class="flex overflow-x-auto">
        <a *ngFor="let s of sections" (click)="scrollTo(s.id)" [class.active]="activeSection === s.id" class="cursor-pointer flex-shrink-0">
          <i class='bx text-base' [class]="s.icon"></i> {{ s.label }}
        </a>
      </div>
    </div>
  </nav>

  <!-- Content -->
  <div class="max-w-7xl mx-auto px-6 py-20 space-y-28">

    <!-- Overview -->
    <section id="overview" class="section-anchor">
      <div class="grid md:grid-cols-2 gap-12 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">What Is CAP</span>
          <h2 class="text-4xl font-black mt-3 mb-5">Marrying Context &amp; Customer Engagement</h2>
          <p class="text-varroc-text-gray leading-relaxed mb-5">The Customer Adoption Program brings together the critical layers needed to operate and scale a connected mobility ecosystem. It ensures that every user experiences a meaningful, personalized, and high-value journey throughout the lifecycle of the vehicle and its digital services.</p>
          <p class="text-varroc-text-gray leading-relaxed">CAP transforms raw telematics and contextual data into actionable engagement, lifecycle intelligence, and monetization opportunities for OEMs and end customers.</p>
        </div>
        <div class="grid grid-cols-2 gap-4" data-aos="fade-left">
          <div class="feature-card text-center">
            <i class='bx bx-headphone icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Support</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-code-block icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">SDK Integration</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-heart icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Engagement</div>
          </div>
          <div class="feature-card text-center">
            <i class='bx bx-line-chart icon-gradient text-3xl mb-3'></i>
            <div class="text-white font-bold text-sm">Monetization</div>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- CAP Pillars -->
    <section id="pillars" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Framework</span>
        <h2 class="text-4xl font-black mt-2 mb-4">CAP Pillars</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">Six connected pillars that combine customer touchpoints, lifecycle intelligence, and OEM value creation in one framework.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div *ngFor="let pillar of capPillars; let i = index" class="feature-card" data-aos="fade-up" [attr.data-aos-delay]="i * 60">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center mb-4">
            <i class='bx icon-gradient text-2xl' [class]="pillar.icon"></i>
          </div>
          <h3 class="text-white font-bold text-lg mb-2">{{ pillar.title }}</h3>
          <p class="text-varroc-text-gray text-sm leading-relaxed">{{ pillar.desc }}</p>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Lifecycle -->
    <section id="lifecycle" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Full Coverage</span>
        <h2 class="text-4xl font-black mt-2 mb-4">Customer Lifecycle</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">CAP equips OEMs with the tools to manage and optimize the full customer lifecycle across connected vehicle programs.</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div *ngFor="let stage of lifecycleStages; let i = index" class="feature-card flex items-start gap-4" data-aos="fade-up" [attr.data-aos-delay]="i * 60">
          <div class="w-10 h-10 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <i class='bx icon-gradient text-xl' [class]="stage.icon"></i>
          </div>
          <div>
            <h3 class="text-white font-semibold text-sm mb-1">{{ stage.stage }}</h3>
            <p class="text-varroc-text-gray text-xs leading-relaxed">{{ stage.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Engagement Model -->
    <section id="engagement" class="section-anchor">
      <div class="mb-12" data-aos="fade-up">
        <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Operating Model</span>
        <h2 class="text-4xl font-black mt-2 mb-4">End-to-End Customer Engagement</h2>
        <p class="text-varroc-text-gray max-w-2xl mr-auto">CAP aligns support, engagement, analytics, and monetization into one operating model for connected mobility programs.</p>
      </div>
      <div class="grid md:grid-cols-2 gap-6">
        <div *ngFor="let item of engagementModel; let i = index" class="feature-card flex items-start gap-5" data-aos="fade-up" [attr.data-aos-delay]="i * 80">
          <div class="w-12 h-12 bg-varroc-accent/15 rounded-xl flex items-center justify-center flex-shrink-0">
            <i class='bx icon-gradient text-2xl' [class]="item.icon"></i>
          </div>
          <div>
            <h3 class="text-white font-bold text-lg mb-2">{{ item.title }}</h3>
            <p class="text-varroc-text-gray text-sm leading-relaxed">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="accent-divider"></div>

    <!-- Business Value -->
    <section id="value" class="section-anchor">
      <div class="grid md:grid-cols-2 gap-12 items-start">
        <div data-aos="fade-right">
          <span class="text-varroc-accent text-sm font-bold uppercase tracking-widest">Business Impact</span>
          <h2 class="text-4xl font-black mt-3 mb-5">Why CAP Matters</h2>
          <p class="text-varroc-text-gray leading-relaxed">CAP gives OEMs a structured model to improve engagement performance, unlock monetization, and run lifecycle programs with stronger operational clarity and connected-vehicle domain support.</p>
        </div>
        <div class="feature-card" data-aos="fade-left">
          <h3 class="text-white font-bold text-lg mb-4">Program Value</h3>
          <ul class="space-y-3">
            <li *ngFor="let point of businessValue" class="flex items-start gap-3 text-varroc-text-gray text-sm">
              <i class='bx bx-check-circle icon-gradient mt-0.5 flex-shrink-0'></i>
              <span>{{ point }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <!-- <section class="text-center py-16 bg-gradient-to-r from-varroc-accent/10 via-varroc-dark to-varroc-accent/10 border border-varroc-accent/20 rounded-3xl" data-aos="fade-up">
      <h2 class="text-4xl font-black mb-4">Launch a Complete Customer Program</h2>
      <p class="text-varroc-text-gray text-lg mb-8 max-w-2xl mx-auto">CAP gives your connected mobility product the structure to onboard, engage, analyze, and monetize customers across the full lifecycle.</p>
      <div class="flex flex-wrap gap-4 justify-center">
        <a routerLink="/contact-us" class="bg-varroc-accent text-white font-bold px-10 py-4 rounded-md hover:bg-varroc-accent-hover hover:shadow-neon transition-all text-lg">Talk to Team</a>
        <a routerLink="/products/connectiq" class="border border-varroc-accent/40 font-semibold px-10 py-4 rounded-md hover:border-varroc-accent hover:bg-varroc-accent/10 transition-all text-lg">Back to ConnectIQ</a>
      </div>
    </section> -->

  </div>
</main>
